/**
 * The wear family — D143 (`docs/design/the-descent-arc.md` §6; Mike's
 * ruling: a general equip primitive with slots — the gas mask first, knives
 * and armor after).
 *
 * @remarks
 * Equipping is a fact about a CARRIED thing: it stays in the hands, and it
 * is also in its slot. The refusal ladder, in order — not carried (R25's own
 * line: the player is not holding it), not equippable (the thing, not the
 * verb: `wear` is a directive now), already worn, the slot holds something
 * else (take that off first) — then the event lines and the item's on-equip
 * effects, folded in order exactly as a `use` interaction folds its own.
 * Taking off mirrors it. Dropping an equipped thing takes it off first,
 * silently, in `handleDrop`: the drop line is the event.
 *
 * @packageDocumentation
 */

import type { RuntimeState } from '@orpheus/content-schema';

import type { RuntimeAction } from '../actions/runtime-action.js';
import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { requireItem } from '../bundle/bundle-lookups.js';
import { speakSlot } from '../defaults/default-responses.js';
import { slotWearing, unequipItem } from '../entities/item-state.js';
import { type ActionHandlerResult, completedSpeaking, rejected } from './action-handler-result.js';
import { type TranscriptCursor } from './transcript-cursor.js';
import { withEffects } from './with-effects.js';

/**
 * The wear pair, one entry — the dispatcher's arm for both directions, as
 * the doors and the switches each have one.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the act
 * @param action - The narrowed action, either direction
 * @param cursor - Where the lines land
 * @returns The handled action
 */
export const handleWear = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	action: Extract<RuntimeAction, { type: 'equip' | 'unequip' }>,
	cursor: TranscriptCursor,
): ActionHandlerResult =>
	action.type === 'equip' ? handleEquip(bundle, state, action, cursor) : handleUnequip(bundle, state, action, cursor);

/**
 * `equip` / `wear` / `put on` — into the item's slot, if the hands hold it
 * and the slot is free.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the act
 * @param action - The narrowed action
 * @param cursor - Where the lines land
 * @returns The handled action
 */
export const handleEquip = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	action: Extract<RuntimeAction, { type: 'equip' }>,
	cursor: TranscriptCursor,
): ActionHandlerResult => {
	const item = requireItem(bundle, action.itemId);
	const { equippable } = item;

	if (!state.world.inventoryItemIds.includes(action.itemId)) {
		return rejected(bundle, state, { itemId: action.itemId, type: 'not_carried' }, [
			speakSlot(bundle, state, 'notCarried', { itemName: item.name }),
		]);
	}

	if (equippable === undefined) {
		return rejected(bundle, state, { itemId: action.itemId, type: 'not_equippable' }, [
			speakSlot(bundle, state, 'notWearable', { itemName: item.name }),
		]);
	}

	if (slotWearing(state, action.itemId) !== undefined) {
		return rejected(bundle, state, { itemId: action.itemId, type: 'already_equipped' }, [
			...(equippable.alreadyEquippedNarratives ?? [speakSlot(bundle, state, 'alreadyWorn', { itemName: item.name })]),
		]);
	}

	const occupyingItemId = state.world.equippedItemIds[equippable.slot];

	if (occupyingItemId !== undefined) {
		return rejected(bundle, state, { itemId: action.itemId, occupyingItemId, type: 'slot_occupied' }, [
			...(equippable.slotOccupiedNarratives ?? [
				speakSlot(bundle, state, 'slotOccupied', {
					itemName: item.name,
					wornItemName: requireItem(bundle, occupyingItemId).name,
				}),
			]),
		]);
	}

	const worn: RuntimeState = {
		...state,
		world: { ...state.world, equippedItemIds: { ...state.world.equippedItemIds, [equippable.slot]: action.itemId }},
	};

	return withEffects(
		bundle,
		completedSpeaking(
			bundle,
			worn,
			cursor,
			equippable.equipNarratives ?? [speakSlot(bundle, state, 'equipped', { itemName: item.name })],
			`equip:${action.itemId}`,
		),
		equippable.onEquip,
	);
};

/**
 * `unequip` / `take off` — out of its slot; the thing stays in the hands.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the act
 * @param action - The narrowed action
 * @param cursor - Where the lines land
 * @returns The handled action
 */
export const handleUnequip = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	action: Extract<RuntimeAction, { type: 'unequip' }>,
	cursor: TranscriptCursor,
): ActionHandlerResult => {
	const item = requireItem(bundle, action.itemId);
	const slot = slotWearing(state, action.itemId);

	const notWorn = () =>
		rejected(bundle, state, { itemId: action.itemId, type: 'not_equipped' }, [speakSlot(bundle, state, 'notWorn', { itemName: item.name })]);

	if (slot === undefined) {
		return notWorn();
	}

	/*
	 * Worn implies equippable. `slotWearing` answers only for an item the state
	 * has put in a slot, and only an item DECLARING `equippable` can be put in
	 * one — so past the guard above this is present, and the question below is
	 * the type's rather than the runtime's. Asked once here instead of twice
	 * inline, because the same unreachable `?.` written at both use sites reads
	 * like a doubt about the invariant rather than a note about the type.
	 */
	const worn = item.equippable;

	/* v8 ignore next 3 */
	if (worn === undefined) {
		return notWorn();
	}

	return withEffects(
		bundle,
		completedSpeaking(
			bundle,
			unequipItem(state, action.itemId),
			cursor,
			worn.unequipNarratives ?? [speakSlot(bundle, state, 'unequipped', { itemName: item.name })],
			`unequip:${action.itemId}`,
		),
		worn.onUnequip,
	);
};
